// crypto.js — Browser-only AES-GCM with optional Base64 salt

const enc = new TextEncoder()
const dec = new TextDecoder()

function bufToBase64(buf) {
  return btoa(String.fromCharCode(...new Uint8Array(buf)))
}

function base64ToBuf(b64) {
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
}

function normalizeSalt(base64Salt) {
  if (!base64Salt) {
    return crypto.getRandomValues(new Uint8Array(16))
  }
  if (typeof base64Salt !== 'string') {
    throw new Error('Salt must be a Base64 string')
  }
  return base64ToBuf(base64Salt)
}

async function deriveKey(password, salt) {
  const keyMaterial = await crypto.subtle.importKey('raw', enc.encode(password), 'PBKDF2', false, ['deriveKey'])

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: 150000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  )
}

export async function encrypt(msg, password, base64Salt = null) {
  if (!msg) throw new Error('Encryption error: Message is empty')
  if (!password) throw new Error('Encryption error: Password is empty')

  const salt = normalizeSalt(base64Salt)
  const iv = crypto.getRandomValues(new Uint8Array(12))

  const key = await deriveKey(password, salt)

  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(msg))

  return JSON.stringify({
    s: bufToBase64(salt), // always include for storage
    i: bufToBase64(iv),
    c: bufToBase64(ciphertext),
  })
}

export async function decrypt(transitmessage, password) {
  if (!transitmessage) throw new Error('Decryption error: Message is empty')
  if (!password) throw new Error('Decryption error: Password is empty')

  try {
    const parsed = JSON.parse(transitmessage)

    const salt = base64ToBuf(parsed.s)

    const iv = base64ToBuf(parsed.i)
    const ciphertext = base64ToBuf(parsed.c)

    const key = await deriveKey(password, salt)

    const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext)

    return dec.decode(plaintext)
  } catch {
    throw new Error('Could not decrypt data: wrong password or corrupted data')
  }
}
